'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { TabText, TabTextItem } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { CurationList } from './CurationList';

type CurationTabType = 'GOODS' | 'SNAP2' | undefined;

const TAB_NAME_MAP: Record<string, 'total' | 'goods' | 'snap'> = {
  curation_tab_total: 'total',
  curation_tab_goods: 'goods',
  curation_tab_snap: 'snap',
};

interface Props {
  snsTitle: string;
  curationId?: string;
}

export const ShopCurationTabsAndList = ({ snsTitle, curationId }: Props) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const is29CM = service === SERVICE['29CM'];
  const [tabType, setTabType] = useState<CurationTabType>(undefined);

  const visibilityQuery = useQuery({
    ...curatorQueries.curations.curationTabsVisibility({ snsTitle }),
    enabled: isMusinsa && Boolean(snsTitle),
  });

  const showTabs = isMusinsa && visibilityQuery.data?.isVisible === true;
  const apiType = showTabs ? tabType : undefined;

  const onTab = useCallback(
    (next: CurationTabType, buttonId: string, buttonName: string) => {
      if (isMusinsa) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'curation_tab',
          component_name: 'tab',
          event_name: 'click_button',
          from: getScreenName(),
          id: snsTitle,
          name: TAB_NAME_MAP[buttonId] ?? 'total',
          section_name: 'curator_curationfeed',
        });
      }
      if (is29CM) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            page_name: 'curator_shop',
            section_name: 'CurationTab',
            section_title: '큐레이션 탭',
            button_id: buttonId,
            button_name: buttonName,
            position: -1,
          },
        });
      }
      setTabType(next);
    },
    [is29CM, isMusinsa, snsTitle],
  );

  return (
    <div className={showTabs ? 'min-h-[12px] bg-[#f5f5f5]' : undefined}>
      {showTabs && (
        <TabText colorScheme="gray" depth="oneDepth" divider="on">
          <TabTextItem
            isSelected={tabType === undefined}
            disabledUnderline={false}
            onClick={() => onTab(undefined, 'curation_tab_total', '전체')}
          >
            전체
          </TabTextItem>
          <TabTextItem
            isSelected={tabType === 'GOODS'}
            disabledUnderline={false}
            onClick={() => onTab('GOODS', 'curation_tab_goods', '상품')}
          >
            상품
          </TabTextItem>
          <TabTextItem
            isSelected={tabType === 'SNAP2'}
            disabledUnderline={false}
            onClick={() => onTab('SNAP2', 'curation_tab_snap', '스냅')}
          >
            스냅
          </TabTextItem>
        </TabText>
      )}
      <CurationList snsTitle={snsTitle} curationId={curationId} type={apiType} />
    </div>
  );
};
